import { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

import RouterLink from './Link';

import { useAppSelector, useAppDispatch } from '@/redux/store.hook';
import { signOut, getAuthSelector } from '@/redux/auth/auth.slice';
import { clearCart } from '@/redux/cart/cart.slice';
import { useLocation, useNavigate } from 'react-router-dom';
import { disconnect } from '@/redux/notification/notification.slice';
import useResize from '@/hooks/useResize';
import { Burger, User } from '@/components/Icon';
import { theme } from '@/theme/Theme';
import { Flex } from '../Flex';
import LanguageSelector from '@/components/UI/LanguageSelector';
import useCurrentLang from '@/hooks/useCurrentLang';
import { filterAccountMenuLinks } from './accountMenu';
import { getAuthenticatedUserDisplayName } from './headerLayout';
import PATH from '@/router/path';
import { hasMultipleApplications } from '@/utils/applications';

const NavbarWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
`;

const DesktopNavbar = styled.div<{ $authenticated: boolean }>`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: flex-end;
  gap: ${(p) => (p.$authenticated ? '30px' : '18px')};
  min-width: 0;
`;

const DesktopUtilityRow = styled.div<{ $authenticated: boolean }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
  width: 100%;
  min-height: ${(p) => (p.$authenticated ? 'auto' : '70px')};
  padding-left: ${(p) => (p.$authenticated ? '0' : 'min(700px, calc(100% - 350px))')};
`;

const NavbarLinks = styled.ul`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  column-gap: 30px;
  row-gap: 12px;
  width: 100%;
  margin: 0;
  padding: 0;
  font-size: ${(p) => p.theme.fontSizes.md};

  @media (max-width: 1600px) {
    column-gap: 15px;
  }
`;

const NavbarLink = styled.li`
  position: relative;
  white-space: nowrap;

  button {
    color: ${(p) => p.theme.colors.gray500};
    :hover {
      color: ${(p) => p.theme.colors.secondary500};
    }
  }
`;

const NavbarDropdownWrapper = styled(Menu)`
  padding: 20px 25px;
  box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.07);
  border-radius: 5px;
  border: none;
`;

const NavbarDropdownLink = styled(MenuItem)`
  :hover {
    color: ${(p) => p.theme.colors.secondary500};
  }
`;

const TrialButton = styled.button`
  padding: 10px 30px;
  background: ${(p) => p.theme.colors.primary500};
  border-radius: 5px;
  color: ${(p) => p.theme.colors.light};
  font-weight: bold;
  cursor: pointer;
`;

const RegisterButton = styled.button`
  padding: 10px 30px;
  background: ${(p) => p.theme.colors.light};
  border-radius: 5px;
  color: ${(p) => p.theme.colors.gray500};
  border: 1px solid ${(p) => p.theme.colors.gray500};
  font-weight: bold;
  cursor: pointer;
`;

interface NavProps {
  layoutController: boolean;
  registDone: boolean;
  burgerOpen: boolean;
  userOpen: boolean;
  isLogin: boolean;
  accessToken?: string;
  showNavigation: boolean;
  setBurgerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUserOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface NavbarProps extends NavProps {
  useMobileNavigation: boolean;
}

const Navbar = ({
  isLogin,
  accessToken,
  userOpen,
  setUserOpen,
  burgerOpen,
  setBurgerOpen,
  layoutController,
  registDone,
  showNavigation,
  useMobileNavigation,
}: NavbarProps) => {
  return (
    <>
      {useMobileNavigation ? (
        <MobileNavbar
          burgerOpen={burgerOpen}
          setBurgerOpen={setBurgerOpen}
          layoutController={layoutController}
          registDone={registDone}
          userOpen={userOpen}
          isLogin={isLogin}
          accessToken={accessToken}
          setUserOpen={setUserOpen}
          showNavigation={showNavigation}
        />
      ) : (
        <PCNavbar
          isLogin={isLogin}
          accessToken={accessToken}
          layoutController={layoutController}
          registDone={registDone}
          showNavigation={showNavigation}
        />
      )}
    </>
  );
};

const MobileNavbar = ({
  isLogin,
  accessToken,
  userOpen,
  setUserOpen,
  burgerOpen,
  setBurgerOpen,
  layoutController,
  registDone,
  showNavigation,
}: NavProps) => {
  const { isMobile } = useResize();
  const auth = useAppSelector(getAuthSelector);
  const { userData } = auth;
  const displayName = getAuthenticatedUserDisplayName(userData);

  if (!showNavigation) return null;

  return (
    <Flex gridGap={isMobile ? 10 : 15}>
      {isLogin && displayName && (
        <button id="basic-button" onClick={() => setUserOpen(!userOpen)}>
          <User.UserSVG
            width={isMobile ? 24 : 48}
            height={isMobile ? 24 : 48}
            fill={userOpen ? theme.colors.secondary500 : theme.colors.primary500}
          />
        </button>
      )}

      <button id="basic-button" onClick={() => setBurgerOpen(!burgerOpen)}>
        <Burger.BurgerSVG
          width={isMobile ? 30 : 48}
          height={isMobile ? 30 : 48}
          fill={burgerOpen ? theme.colors.secondary500 : theme.colors.primary500}
        />
      </button>
    </Flex>
  );
};

const PCNavbar = ({
  layoutController,
  registDone,
  isLogin,
  accessToken,
  showNavigation,
}: Omit<NavProps, 'setBurgerOpen' | 'burgerOpen' | 'setUserOpen' | 'userOpen'>) => {
  const auth = useAppSelector(getAuthSelector);
  // @ts-ignore
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const memberLinks = filterAccountMenuLinks(
    t('pages.navbar.memberLinks', { returnObjects: true }) as any[],
    auth.userData,
    { activeApplication: auth.activeApplication, pathname }
  );
  const barlinks = t('pages.navbar.links', { returnObjects: true });
  const currentLang = useCurrentLang();
  const displayName = getAuthenticatedUserDisplayName(auth.userData);
  const showSwitchApplication = auth.isLogin && hasMultipleApplications(auth.applications);

  const [anchorEl, setAnchorEl] = useState<null | { [x: number]: HTMLElement }>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>, index: number) => {
    setAnchorEl({ [index]: event.currentTarget });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const userSignout = () => {
    dispatch(disconnect());
    dispatch(signOut());
    dispatch(clearCart());

    // 一秒後再跳轉
    setTimeout(() => {
      window.location.href = '/';
    }, 1000);
  };

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toBottom = () => {
    window.scrollTo({ top: 100000, behavior: 'smooth' });
  };

  return (
    <DesktopNavbar $authenticated={layoutController}>
      <DesktopUtilityRow $authenticated={layoutController}>
        {showNavigation && registDone && !layoutController && !auth.isLogin && (
          <>
            <RouterLink to="/auth/signin">
              <TrialButton>{t('buttons.signIn')}</TrialButton>
            </RouterLink>

            <RouterLink to="/auth/signup">
              <RegisterButton>{t('buttons.register')}</RegisterButton>
            </RouterLink>
          </>
        )}
        <LanguageSelector theme="dark" inline />
      </DesktopUtilityRow>
      {showNavigation && registDone && (
        <NavbarWrapper>
          <NavbarLinks>
            {layoutController ? (
              <>
                <NavbarLink style={{ color: '#0B319D' }}>{displayName}</NavbarLink>
                {memberLinks.map((link, key) => (
                  <NavbarLink key={`m_${link.name}`}>
                    <button
                      type="button"
                      onClick={(e) => handleClick(e, key)}
                      style={{ cursor: 'pointer', fontWeight: 700 }}
                    >
                      {link.name}
                    </button>
                    <NavbarDropdownWrapper
                      anchorEl={anchorEl && anchorEl[key]}
                      open={anchorEl ? Boolean(anchorEl[key]) : false}
                      onClose={handleClose}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                      PaperProps={{
                        style: {
                          transform: 'translateX(0) translateY(20px)',
                        },
                      }}
                    >
                      {showSwitchApplication && (
                        <NavbarDropdownLink
                          key="switch-application"
                          onClick={() => {
                            handleClose();
                            navigate(PATH.application.select);
                          }}
                        >
                          Switch Application
                        </NavbarDropdownLink>
                      )}
                      {link.children.map((cLink: any, cKey: number) => (
                        <NavbarDropdownLink
                          key={`mc_${cLink.name}`}
                          onClick={() => {
                            if (memberLinks[key].children[cKey].url === '/#') {
                              handleClose();
                              userSignout();
                            } else {
                              handleClose();
                              navigate(auth.isLogin && accessToken !== '' ? cLink.url : '/');
                            }
                          }}
                        >
                          {cLink.name}
                        </NavbarDropdownLink>
                      ))}
                    </NavbarDropdownWrapper>
                  </NavbarLink>
                ))}
                {/* <Link to="/cart">
                  <img width="32px" src={Cart.CartSVG} alt="" />
                </Link> */}
              </>
            ) : (
              barlinks.map((link) => (
                <NavbarLink key={`l_${link.name}`}>
                  <RouterLink
                    onClick={link.url !== '/About#Contact' ? toTop : toBottom}
                    to={`/${currentLang}${link.url}`}
                  >
                    {link.name}
                  </RouterLink>
                </NavbarLink>
              ))
            )}
          </NavbarLinks>
        </NavbarWrapper>
      )}
    </DesktopNavbar>
  );
};

export default Navbar;
